import { useMemo } from "react";
import { DataTable, Pagination } from "@/components/molecules";
import { usePagination, useProfile } from "@/hooks";
import { useAppSelector } from "@/redux/hooks";
import { addToast, clsx, downloadFile } from "@/utils";
import { MonthlyInvoiceItem } from "@/redux/slices/payment/types";
import { monthlyInvoicePageSelector } from "@/redux/slices/payment/selectors";
import { generateColumns, generateData } from "./helpers";
import { useLazyGetMonthlyInvoiceItemQuery } from "@/redux/apis/payment/paymentApi";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { Loading } from "@/components/atoms";

interface MonthlyInvoiceTableProps extends React.HTMLAttributes<HTMLDivElement> {
  items: MonthlyInvoiceItem[];
}

export const MonthlyInvoiceTable = ({ items, className, ...props }: MonthlyInvoiceTableProps) => {
  const { dateFormat } = useProfile();
  const [getMonthlyInvoiceItem, { isFetching }] = useLazyGetMonthlyInvoiceItemQuery();

  const handleDownload = (id: string, documentType: string) => {
    getMonthlyInvoiceItem(id)
      .unwrap()
      .then((res) => {
        if (documentType === "pdf") downloadFile(res.documents.pdf, res.documents.pdf);
        else if (documentType === "csv") downloadFile(res.documents.csv, res.documents.csv);
        else addToast("danger", "Invalid document type");
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const data = useMemo(() => 
    generateData(items, dateFormat, handleDownload), 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [items]);
  const columns = useMemo(() => generateColumns(), []);

  return (
    <div className={clsx("relative flex bg-white", className)}>
      {isFetching && <Loading className="rounded-lg" />}
      <DataTable 
        className="[&_table]:table-auto overflow-x-auto w-full"
        columns={columns} 
        data={data} 
        {...props} 
      />
    </div>
  );
};

const MonthlyInvoiceTablePagination = () => {
  const { current, count, total, size } = useAppSelector(monthlyInvoicePageSelector);
  const { handlePageClick, handleSizeChange } = usePagination("monthly-invoice");

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      totalItems={total}
      pageCount={count}
    />
  );
};

MonthlyInvoiceTable.Pagination = MonthlyInvoiceTablePagination;
